import React, { useState, useRef, useEffect } from 'react';
import {
  CircleDashed, CheckCircle, User, Paperclip,
  Trash, PencilSimple, X, Question, Sparkle, Robot, CaretLeft, CaretRight, Check
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DASHBOARD } from '@/constants/testIds';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch (e) {
    return '';
  }
};

const looksLikeInstruction = (text) => {
  const kw = /(build|create|make|add|generate|design|implement|refactor|fix|develop|write|convert|migrate|deploy)/i;
  return kw.test(text) && text.trim().split(/\s+/).length >= 3;
};

const buildStructuredInstruction = (userText, revisionNote) => {
  const base = `Task: ${userText.trim()}`;
  const details = revisionNote
    ? `\n\nRevision incorporated: ${revisionNote}`
    : '';
  return `${base}${details}\n\nConstraints:\n• Follow project conventions\n• Include tests where relevant\n• Preserve previously approved artifacts\n\nDefinition of done: A reviewable artifact produced in Demo Mode, ready for the Pre-Deployment Sandbox.`;
};

const MessageBubble = ({ msg, onApproveSend }) => {
  const isHuman = msg.sender === 'human';
  const isSystem = msg.sender === 'system';
  const align = isHuman ? 'ml-4' : 'mr-4';
  const bg = isSystem ? 'bg-[#13131A] border-[#00E5FF]' : (isHuman ? 'bg-[#1C1C24]' : 'bg-[#13131A]');

  const showHandoffAction =
    msg.sender === 'origin_ai' &&
    msg.id &&
    typeof msg.id === 'string' &&
    msg.id !== 'demo-conn' &&
    msg.content &&
    msg.content.trim().length > 0;

  return (
    <div className={`p-3 border border-[#1F2937] ${bg} ${align}`}>
      <div className="flex items-center gap-2 mb-1">
        {isHuman && <User size={12} className="text-[#00E5FF]" weight="fill" />}
        {!isHuman && !isSystem && <Robot size={12} className="text-[#D97706]" weight="fill" />}
        <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] terminal-font">
          {isHuman ? 'YOU' : isSystem ? 'SYSTEM' : 'ORIGIN AI'}
        </span>
        <span className="text-[10px] text-[#9CA3AF] terminal-font ml-auto">{formatTime(msg.timestamp)}</span>
      </div>
      <p className="text-sm body-font text-[#F3F4F6] leading-relaxed whitespace-pre-line">{msg.content}</p>
      {msg.attachments?.length > 0 && (
        <div className="mt-2 space-y-1">
          {msg.attachments.map((a) => (
            <div key={a} className="flex items-center gap-1.5 text-[10px] terminal-font text-[#00E5FF]">
              <Paperclip size={10} />
              {a}
            </div>
          ))}
        </div>
      )}
      {msg.status && (
        <div className="mt-1 text-[10px] terminal-font text-[#9CA3AF] uppercase tracking-wider">
          Status: <span className="text-[#F3F4F6]">{msg.status}</span>
        </div>
      )}
      {showHandoffAction && onApproveSend && (
        <div className="mt-3 pt-2 border-t border-[#1F2937] flex gap-2">
          <Button
            size="sm"
            onClick={() => onApproveSend(msg.id, msg.content)}
            className="text-[10px] h-6 bg-[#10B981] text-[#0A0C10] hover:bg-[#10B981]/90 font-bold terminal-font"
          >
            <Check size={10} className="mr-1" />
            Approve &amp; Send
          </Button>
        </div>
      )}
    </div>
  );
};

const InstructionCard = ({ card, onEdit, onApproveSend, onReject, onRequestClarification, onSaveEdit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(card.content);
  const [clarifying, setClarifying] = useState(false);
  const [clarification, setClarification] = useState('');

  const statusColor = {
    pending: 'text-[#D97706] border-[#D97706]',
    approved: 'text-[#10B981] border-[#10B981]',
    rejected: 'text-[#F85149] border-[#F85149]'
  }[card.status] || 'text-[#9CA3AF] border-[#1F2937]';

  const handleSave = () => {
    onSaveEdit(card.id, draft);
    setEditing(false);
  };

  const handleSubmitClarification = () => {
    if (!clarification.trim()) return;
    onRequestClarification(card.id, clarification.trim());
    setClarification('');
    setClarifying(false);
  };

  return (
    <div
      data-testid={DASHBOARD.originInstructionCard}
      className={`border ${statusColor} bg-[#0A0C10] p-3 mr-2`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkle size={12} className="text-[#00E5FF]" weight="fill" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#00E5FF] terminal-font font-bold">
            Instruction Proposal
          </span>
        </div>
        <OperationalStatusBadge status={OPERATIONAL_STATUS.DEMO} size="sm" />
      </div>

      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="bg-[#13131A] border-[#1F2937] text-xs font-mono text-[#F3F4F6] min-h-[100px]"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(false)}
              className="text-[10px] h-6 terminal-font border-[#1F2937]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              className="text-[10px] h-6 bg-[#00E5FF] text-[#0A0C10] font-bold terminal-font"
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs body-font text-[#F3F4F6] whitespace-pre-line mb-3">{card.content}</p>
      )}

      {clarifying && (
        <div className="mt-2 p-2 bg-[#13131A] border border-[#1F2937] space-y-2">
          <Textarea
            placeholder="Specify what clarification is needed..."
            value={clarification}
            onChange={(e) => setClarification(e.target.value)}
            className="bg-[#0A0C10] border-[#1F2937] text-xs font-mono text-[#F3F4F6] min-h-[60px]"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setClarifying(false)}
              className="text-[10px] h-6 terminal-font border-[#1F2937]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmitClarification}
              className="text-[10px] h-6 bg-[#00E5FF] text-[#0A0C10] font-bold terminal-font"
            >
              Send Clarification
            </Button>
          </div>
        </div>
      )}

      {!editing && !clarifying && card.status === 'pending' && (
        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-[#1F2937]">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="text-[10px] h-6 border-[#1F2937] text-[#9CA3AF] hover:text-[#F3F4F6] terminal-font"
          >
            <PencilSimple size={10} className="mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            onClick={() => onApproveSend(card.id, card.content)}
            className="text-[10px] h-6 bg-[#10B981] text-[#0A0C10] hover:bg-[#10B981]/90 font-bold terminal-font"
          >
            <Check size={10} className="mr-1" />
            Approve &amp; Send
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setClarifying(true)}
            className="text-[10px] h-6 border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 terminal-font"
          >
            <Question size={10} className="mr-1" />
            Clarify
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReject(card.id)}
            className="text-[10px] h-6 border-[#F85149]/40 text-[#F85149] hover:bg-[#F85149]/10 terminal-font"
          >
            <X size={10} className="mr-1" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
};

const OriginAIPanel = ({ session, messages, onConnect, onDisconnect, onSendMessage, onHandoffRequest, activeExecution, onCancelExecution, isCollapsed = false, onToggleCollapse }) => {
  const [chat, setChat] = useState([
    {
      id: 1,
      kind: 'message',
      sender: 'origin_ai',
      content: 'Welcome to AI Bridge Hub Fusion. Describe your goal or type "build a task dashboard" to generate a reviewable instruction card.',
      timestamp: new Date().toISOString()
    }
  ]);
  const [draft, setDraft] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const scrollRef = useRef(null);

  const isConnected = session?.origin_ai?.status === 'connected';
  const isRealVerified = session?.origin_ai?.mode === 'api' && session?.origin_ai?.verified === true;

  const isExecuting = activeExecution && !['completed', 'failed', 'timed_out', 'cancelled'].includes(activeExecution.status);
  
  const getExecutionStatusText = (status) => {
    switch (status) {
      case 'accepting': return 'Preparando mensaje...';
      case 'pending': return 'Mensaje aceptado y en espera.';
      case 'running': return 'Procesando respuesta.';
      case 'completed': return 'Respuesta IA incorporada una sola vez a la conversación.';
      case 'failed': return `Ejecución fallida: ${activeExecution?.error_message || 'Error desconocido'}`;
      case 'timed_out': return 'La ejecución excedió el tiempo permitido.';
      case 'cancel_requested': return 'Cancelación solicitada.';
      case 'cancelled': return 'Ejecución cancelada.';
      default: return 'Ejecutando...';
    }
  };

  useEffect(() => {
    if (!isCollapsed) {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chat, isCollapsed]);

  useEffect(() => {
    // Si la sesión real está verificada, limpiamos los mensajes de bienvenida y conexión de DEMO.
    if (isRealVerified) {
      setChat(prevChat => prevChat.filter(m => m.id !== 1 && m.id !== 'demo-conn'));
    }
  }, [isRealVerified]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      setChat(prevChat => {
        // Normalizamos los mensajes. El backend envía 'source' en lugar de 'sender'.
        const normalized = messages.map(m => ({
          id: m.id,
          kind: 'message',
          sender: m.source || m.sender,
          content: m.content || m.text,
          timestamp: m.timestamp
        }));

        const newMessages = normalized.filter(
          m => m.sender === 'origin_ai' && !prevChat.some(c => c.id === m.id)
        );
        if (newMessages.length === 0) return prevChat;
        
        return [...prevChat, ...newMessages];
      });
    }
  }, [messages]);

  if (isCollapsed) {
    return (
      <div 
        data-testid={DASHBOARD.originPanel}
        onClick={onToggleCollapse}
        className="h-full w-full bg-[#0E1117] border-r border-white/[0.08] flex flex-col items-center py-3 cursor-pointer hover:border-white/[0.15] transition-all select-none group gap-3"
        title="Expandir Panel Origin AI"
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          className="p-1.5 hover:bg-white/[0.06] text-slate-400 hover:text-white rounded-md transition-all"
        >
          <CaretRight size={14} />
        </button>
        <div className="flex-1 flex flex-col items-center gap-2">
          <Robot size={16} className="text-slate-400 group-hover:text-cyan-400 transition-colors" />
          <span
            data-testid="origin-status-dot"
            className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-slate-600'}`}
            title={isConnected ? 'Connected' : 'Disconnected'}
          />
        </div>
      </div>
    );
  }

  const appendChat = (item) => {
    setChat((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...item
      }
    ]);
  };

  const handleConnect = async () => {
    await onConnect('origin');
    appendChat({
      id: 'demo-conn',
      kind: 'message',
      sender: 'system',
      content: 'Demo Origin AI connected. Describe your goal to start generating instructions.'
    });
  };

  const generateAIReply = (userText) => {
    console.log("generateAIReply called with:", userText);
    if (looksLikeInstruction(userText)) {
      console.log("looksLikeInstruction = true");
      appendChat({
        kind: 'message',
        sender: 'origin_ai',
        content: 'I\'ve drafted a structured production instruction for your review.'
      });
      appendChat({
        kind: 'instruction',
        sender: 'origin_ai',
        content: buildStructuredInstruction(userText),
        status: 'pending',
        originalRequest: userText
      });
    } else {
      console.log("looksLikeInstruction = false");
      appendChat({
        kind: 'message',
        sender: 'origin_ai',
        content: 'Understood. Describe what you want to build (e.g. "build a task dashboard") to generate a structured instruction card.'
      });
    }
  };

  const handleSend = () => {
    console.log("handleSend called, draft:", draft);
    if (!draft.trim()) return;
    
    const userText = draft;
    setDraft('');
    
    appendChat({
      kind: 'message',
      sender: 'human',
      content: userText
    });
    
    if (isRealVerified && onSendMessage) {
      console.log("isRealVerified is true, calling onSendMessage");
      onSendMessage(userText);
    } else {
      console.log("isRealVerified is false, calling setTimeout for generateAIReply");
      const timeoutMs = process.env.NODE_ENV === 'test' ? 10 : 600;
      setTimeout(() => {
        generateAIReply(userText);
      }, timeoutMs);
    }
  };

  const handleApproveSendCard = async (cardId, content) => {
    setChat((prev) =>
      prev.map((item) => (item.id === cardId ? { ...item, status: 'approved' } : item))
    );
    const card = chat.find(c => c.id === cardId);
    const sourceMessageId = card?.id;
    if (onHandoffRequest) {
      await onHandoffRequest(sourceMessageId, content);
    } else if (onSendMessage) {
      await onSendMessage(content);
    }
  };

  const handleSaveEditCard = (cardId, newContent) => {
    setChat((prev) =>
      prev.map((item) => (item.id === cardId ? { ...item, content: newContent } : item))
    );
  };

  const handleRejectCard = (cardId) => {
    setChat((prev) =>
      prev.map((item) => (item.id === cardId ? { ...item, status: 'rejected' } : item))
    );
  };

  const handleRequestClarification = (cardId, note) => {
    const card = chat.find((item) => item.id === cardId);
    if (!card) return;
    appendChat({ kind: 'message', sender: 'human', content: `Clarification: ${note}`, status: 'sent' });
    setTimeout(() => {
      appendChat({
        kind: 'instruction',
        sender: 'origin_ai',
        content: buildStructuredInstruction(card.originalRequest || card.content, note),
        status: 'pending',
        originalRequest: card.originalRequest || card.content
      });
    }, 700);
  };

  return (
    <div
      data-testid={DASHBOARD.originPanel}
      className="h-full overflow-hidden border-r border-[#1F2937] flex flex-col bg-[#0A0C10]"
    >
      {/* Header */}
      <div className="p-4 border-b border-[#1F2937] flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold terminal-font text-[#F3F4F6] uppercase tracking-wider">Origin AI</h2>
          <div className="flex items-center gap-2 mt-1">
            {isConnected ? (
              <CheckCircle size={14} className="text-[#10B981] pulse-glow" weight="fill" />
            ) : (
              <CircleDashed size={14} className="text-[#9CA3AF]" />
            )}
            <span className={`text-xs terminal-font font-bold ${isConnected ? 'text-[#10B981]' : 'text-[#9CA3AF]'}`}>
              {isConnected ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
            {isConnected && (
              isRealVerified ? (
                <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} label="REAL / VERIFIED" size="sm" />
              ) : (
                <OperationalStatusBadge status={OPERATIONAL_STATUS.DEMO} label="DEMO CONNECTED" size="sm" />
              )
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="p-1 hover:bg-[#1C1C24] text-[#9CA3AF] hover:text-[#00E5FF] rounded transition-colors"
              title="Colapsar Panel IA de Origen"
            >
              <CaretLeft size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {!isConnected ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-[#13131A] border border-[#1F2937] flex items-center justify-center mb-4">
            <CircleDashed size={24} className="text-[#9CA3AF] animate-spin" />
          </div>
          <p className="text-sm body-font text-[#9CA3AF] mb-4">
            Conecta Origin AI para iniciar la conversación.
          </p>
          <Button
            data-testid={DASHBOARD.originConnectBtn}
            onClick={handleConnect}
            className="bg-[#00E5FF] text-[#0A0C10] hover:bg-[#00E5FF]/90 terminal-font text-xs font-bold"
          >
            Conectar Origin AI
          </Button>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3 pr-2">
              {chat.map((item) => (
                <React.Fragment key={item.id}>
                  {item.kind === 'instruction' ? (
                    <InstructionCard
                      card={item}
                      onApproveSend={handleApproveSendCard}
                      onSaveEdit={handleSaveEditCard}
                      onReject={handleRejectCard}
                      onRequestClarification={handleRequestClarification}
                    />
                  ) : (
                    <MessageBubble msg={item} onApproveSend={handleApproveSendCard} />
                  )}
                </React.Fragment>
              ))}
              <div ref={scrollRef} />
            </div>
          </ScrollArea>

          {/* Active Execution Banner */}
          {activeExecution && (
            <div className="p-2 border-t border-[#1F2937] bg-[#1C1C24] flex items-center justify-between">
              <span className="text-xs terminal-font text-[#00E5FF]">
                Estado: {getExecutionStatusText(activeExecution.status)}
              </span>
              {activeExecution.cancellable && activeExecution.status !== 'cancel_requested' && !['completed', 'failed', 'timed_out', 'cancelled'].includes(activeExecution.status) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onCancelExecution}
                  className="h-6 text-[10px] border-[#F85149] text-[#F85149] hover:bg-[#F85149]/10 terminal-font flex items-center justify-center"
                >
                  Cancelar
                </Button>
              )}
            </div>
          )}

          {/* User Chat Input Box */}
          <div className="p-3 border-t border-[#1F2937] bg-[#13131A]">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Describe what you want to build..."
              disabled={isExecuting}
              className="bg-[#0A0C10] border-[#1F2937] text-xs font-mono text-[#F3F4F6] min-h-[60px] mb-2 disabled:opacity-50"
            />
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                onClick={handleSend}
                disabled={isExecuting}
                className="bg-[#00E5FF] text-[#0A0C10] hover:bg-[#00E5FF]/90 terminal-font text-xs font-bold ml-auto disabled:opacity-50"
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OriginAIPanel;
